import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import LmsEnrolmentAssignmentsTable from "@/components/lms/lmsEnrolmentAssignmentsTable";
import LmsEnrolmentAssignmentEditModal from "@/components/lms/lmsEnrolmentAssignmentEditModal";
import LmsEnrolmentCreateModal from "@/components/lms/lmsEnrolmentCreateModal";
import { ASSIGNMENT_RULES_EXCLUDED_TYPES } from "@/components/lms/enrolmentAssignmentTargetSelection";
import GenericNotification from "@/components/notification/genericNotification";
import { RootState } from "@/components/state/store";
import {
  getEffectiveLmsMode,
  getLmsCoursesRoute,
  LmsMode,
  shouldBlockLmsForSubdomain,
} from "@/components/lms/lms.utils";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";

interface Props {
  requiredMode?: LmsMode;
}

export default function LmsEnrolmentAssignments({ requiredMode }: Props) {
  const { t } = useTranslation();
  usePageHeading(t("enrolment_assignments"));
  const loginState = useSelector((state: RootState) => state.login);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<any>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [notificationMessage, setNotificationMessage] = useState("");

  const subdomain = window.location.hostname.split(".")[0];
  const shouldBlockLms = shouldBlockLmsForSubdomain({
    subdomain,
    permissions: loginState.permissions,
  });
  const effectiveMode = getEffectiveLmsMode({
    subdomain,
    permissions: loginState.permissions,
  });

  if (shouldBlockLms) {
    return <Navigate to="/" replace />;
  }

  if (requiredMode && effectiveMode !== requiredMode) {
    return <Navigate to={getLmsCoursesRoute(effectiveMode)} replace />;
  }

  const handleRefresh = () => {
    setRefreshKey((currentValue) => currentValue + 1);
  };

  const handleCreateCompleted = () => {
    setIsCreateModalOpen(false);
    handleRefresh();
    setNotificationMessage(t("create_enrolment_assignment_success"));
  };

  const handleEditCompleted = () => {
    setIsEditModalOpen(false);
    setSelectedAssignment(null);
    handleRefresh();
    setNotificationMessage(t("edit_enrolment_assignment_success"));
  };

  return (
    <>
      <LmsEnrolmentAssignmentsTable
        mode={effectiveMode}
        onCreate={() => setIsCreateModalOpen(true)}
        onEdit={(assignment) => {
          setSelectedAssignment(assignment);
          setIsEditModalOpen(true);
        }}
        refreshKey={refreshKey}
        onRefreshed={handleRefresh}
      />

      <LmsEnrolmentCreateModal
        open={isCreateModalOpen}
        mode={effectiveMode}
        onClose={() => setIsCreateModalOpen(false)}
        onCompleted={handleCreateCompleted}
        enableCourseSelection={true}
        excludedAssignmentTypes={ASSIGNMENT_RULES_EXCLUDED_TYPES}
      />

      <LmsEnrolmentAssignmentEditModal
        open={isEditModalOpen}
        mode={effectiveMode}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedAssignment(null);
        }}
        onCompleted={handleEditCompleted}
        assignment={selectedAssignment}
      />

      {notificationMessage && (
        <GenericNotification
          message={notificationMessage}
          variant="success"
          handleCloseSnackbar={() => setNotificationMessage("")}
        />
      )}
    </>
  );
}
